import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../store/slices/authSlice';
import { toast } from 'react-toastify';

const SESSION_TIMEOUT = 60 * 60 * 1000; // 60 minutes in milliseconds

/**
 * Hook to manage session timeout
 * Automatically logs out user after 60 minutes of inactivity
 */
export const useSessionTimeout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const checkIntervalRef = useRef(null);
  const warningShownRef = useRef(false);

  useEffect(() => {
    const checkSessionTimeout = () => {
      const token = localStorage.getItem('token');

      // If no token, user is not logged in
      if (!token) {
        return;
      }

      let loginTimestamp = localStorage.getItem('loginTimestamp');
      
      // If token exists but no login timestamp (e.g., from old session before this feature),
      // set it to current time to start a fresh 60-minute session
      if (!loginTimestamp) {
        loginTimestamp = Date.now().toString();
        localStorage.setItem('loginTimestamp', loginTimestamp);
        console.log('[SESSION] No login timestamp found, setting to current time');
      }

      const now = Date.now();
      const loginTime = parseInt(loginTimestamp, 10);
      const elapsed = now - loginTime;
      const remaining = SESSION_TIMEOUT - elapsed;

      // If session has expired
      if (elapsed >= SESSION_TIMEOUT) {
        console.log('[SESSION] Session expired, logging out...');
        dispatch(logout());
        localStorage.removeItem('token');
        localStorage.removeItem('loginTimestamp');
        toast.error('Your session has expired. Please login again.');
        navigate('/login');
        return;
      }

      // Show warning 5 minutes before expiration (at 55 minutes)
      if (remaining <= 5 * 60 * 1000 && remaining > 4 * 60 * 1000 && !warningShownRef.current) {
        const minutesLeft = Math.ceil(remaining / (60 * 1000));
        toast.warning(`Your session will expire in ${minutesLeft} minute${minutesLeft > 1 ? 's' : ''}. Please save your work.`, {
          autoClose: 60000, // Show for 1 minute
        });
        warningShownRef.current = true;
      }
    };

    // Check immediately
    checkSessionTimeout();

    // Set up interval to check every minute
    checkIntervalRef.current = setInterval(checkSessionTimeout, 60 * 1000);

    // Cleanup on unmount
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [dispatch, navigate]);

  // Reset warning flag when user logs in again
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      warningShownRef.current = false;
    } else {
      warningShownRef.current = false;
    }
  }, []);
};

